"use client";

import * as React from "react";
import { cn, getLocaleUI } from "../utils";
import { useDebouncedOptions } from "../hooks";
import type { BlockBase } from "@/cms/blocks/registry/types";

export function ReferenceEditor({
  block,
  locale,
  onChange,
}: {
  block: BlockBase;
  locale: "en" | "fa";
  onChange: (patch: (data: any) => any) => void;
}) {
  const data = (block.data as any) ?? {};
  const referenceIds: string[] = Array.isArray(data.referenceIds)
    ? data.referenceIds
    : data.referenceId
      ? [data.referenceId]
      : [];
  const {
    dir: inputDir,
    inputClass: localizedInputClass,
  } = getLocaleUI(locale);

  const [query, setQuery] = React.useState("");
  const [selected, setSelected] = React.useState<any[]>([]);

  const fetchOptions = React.useCallback(async (search: string) => {
    const params = new URLSearchParams({
      take: "30",
      active: "true",
    });
    if (search.trim()) params.set("q", search.trim());
    const res = await fetch(`/api/cms/references?${params.toString()}`);
    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || "Failed to load references");
    }
    return Array.isArray(json?.items) ? json.items : [];
  }, []);

  const { results: options, loading, error } = useDebouncedOptions<any>({
    query,
    delay: 250,
    fetcher: fetchOptions,
  });

  const resolveSelected = React.useCallback(async (ids: string[]) => {
    if (!ids.length) {
      setSelected([]);
      return;
    }
    try {
      const res = await fetch(
        `/api/cms/references/resolve?ids=${encodeURIComponent(ids.join(","))}`,
      );
      const json = await res.json();
      const items = Array.isArray(json?.items) ? json.items : [];
      const byId = new Map(items.map((item: any) => [item.id, item]));
      setSelected(ids.map((id) => byId.get(id)).filter(Boolean));
    } catch {
      setSelected([]);
    }
  }, []);

  React.useEffect(() => {
    resolveSelected(referenceIds).catch(() => {});
  }, [referenceIds, resolveSelected]);

  const handleToggle = (ref: any) => {
    const nextIds = referenceIds.includes(ref.id)
      ? referenceIds.filter((id) => id !== ref.id)
      : [...referenceIds, ref.id];
    onChange((prev) => ({ ...prev, referenceIds: nextIds, referenceId: "" }));
    setSelected((prevSelected) => {
      if (nextIds.includes(ref.id)) {
        return prevSelected.some((item) => item.id === ref.id)
          ? prevSelected
          : [...prevSelected, ref];
      }
      return prevSelected.filter((item) => item.id !== ref.id);
    });
  };

  const clearSelection = () => {
    onChange((prev) => ({ ...prev, referenceIds: [], referenceId: "" }));
    setSelected([]);
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <label className="text-xs font-medium text-gray-600">
          Choose references
        </label>
        <input
          dir={inputDir}
          className={`w-full ${localizedInputClass}`}
          placeholder="Search references…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {error ? (
          <div className="text-xs text-red-600">{error}</div>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        {loading ? (
          <div className="text-xs text-gray-500">Loading…</div>
        ) : options.length ? (
          options.map((ref) => (
            <button
              key={ref.id}
              type="button"
              onClick={() => handleToggle(ref)}
              className={cn(
                "rounded border px-3 py-2 text-sm transition",
                referenceIds.includes(ref.id)
                  ? "border-pinkCustom bg-pinkCustom/10 text-pinkCustom"
                  : "border-gray-200 bg-white text-gray-700 hover:border-pinkCustom",
              )}
            >
              {ref.name}
            </button>
          ))
        ) : (
          <div className="text-xs text-gray-500">No references found.</div>
        )}
      </div>

      {selected.length ? (
        <div className="space-y-3">
          {selected.map((item) => {
            const displayName =
              typeof item?.name === "string" ? item.name : item?.slug;
            const localizedLinkLabel =
              locale === "fa" ? item?.linkLabelFa : item?.linkLabelEn;
            const localizedLinkHref =
              locale === "fa" ? item?.linkHrefFa : item?.linkHrefEn;
            return (
              <div
                key={item.id}
                className="rounded-lg border border-gray-200 bg-white p-3"
              >
                <div className="flex items-start gap-3">
                  {item.logoAsset ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={item.logoAsset.url}
                      alt=""
                      className="h-12 w-12 rounded border border-gray-200 object-contain"
                    />
                  ) : null}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slateCustom">
                      {displayName || item.slug_en}
                    </div>
                    {localizedLinkHref ? (
                      <div className="truncate text-xs text-gray-500">
                        {localizedLinkLabel ? `${localizedLinkLabel} · ` : ""}
                        {localizedLinkHref}
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggle(item)}
                    className="ml-auto text-xs text-red-600"
                  >
                    Remove
                  </button>
                </div>
                {item.coverAsset ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={item.coverAsset.url}
                    alt=""
                    className="mt-3 h-28 w-full rounded border border-gray-200 object-cover"
                  />
                ) : null}
              </div>
            );
          })}
          <button
            type="button"
            onClick={clearSelection}
            className="text-xs text-red-600"
          >
            Clear all
          </button>
        </div>
      ) : (
        <div className="rounded border border-dashed border-gray-200 bg-gray-50 p-3 text-xs text-gray-500">
          Select references to attach.
        </div>
      )}

      <div className="rounded border border-dashed border-pinkCustom/50 bg-pinkCustom/5 px-3 py-2 text-xs text-pinkCustom">
        Manage references in CMS settings.
      </div>
    </div>
  );
}
